/**
 * src/features/billing/ui/MarginAnalyticsPanel.tsx
 *
 * Per-project margin analytics panel (SAAS-03, MARGIN-01).
 *
 * States (per UI-SPEC Surface 3, D-05 — trial users get full Pro UX):
 *   A — !entitled: locked card with upgrade CTA.
 *   B — entitled && billedTotal > 0: 2×2 metric grid.
 *   C — entitled && billedTotal === 0: empty state message.
 *   D — loading skeleton (safety fallback; computation is synchronous).
 *
 * Number formatting: roundTo + toLocaleString('pl-PL') (Polish locale).
 * Margin% colour: green (≥0) / amber (<0) per UI-SPEC colour contract.
 * Accessibility: <dl>/<dt>/<dd> for metric pairs; Lock icon aria-hidden="true".
 *
 * INV-1 guard replicated in display: marginPercent for display is derived
 * from props (billedTotal/baseCostTotal) using the same guarded formula
 * as computeMargin — display never diverges from the canonical math.
 *
 * NOTE: entitled is a UX-only gate — server requireAccess is the real gate (T-08-16).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { Card, CardContent } from '@/shared/ui/card';
import Button from '@/shared/ui/button';
import { roundTo } from '@/shared/lib/number';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MarginAnalyticsPanelProps {
  /** Whether the user is entitled to the Pro experience (trial or active subscription). Controls which state is rendered. UX-only — never an authorization source (T-08-16). */
  entitled: boolean;
  /** Project billed total (with user prices + safetyMargin, pre-VAT). */
  billedTotal: number;
  /** Project base cost total (catalog base prices, no safetyMargin, pre-VAT). */
  baseCostTotal: number;
  /** Currency label — default 'PLN'. */
  currency?: string;
  /** Called when free user clicks "Unlock analytics". Opens the upgrade modal. */
  onUpgradeClick: () => void;
}

// ---------------------------------------------------------------------------
// Formatting helpers (UI-SPEC: Polish locale, 2 decimal PLN, 1 decimal margin%)
// ---------------------------------------------------------------------------

function formatPLN(value: number): string {
  return roundTo(value, 2).toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMargin(value: number): string {
  return `${roundTo(value, 1).toLocaleString('pl-PL')}%`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MarginAnalyticsPanel({
  entitled,
  billedTotal,
  baseCostTotal,
  currency = 'PLN',
  onUpgradeClick,
}: MarginAnalyticsPanelProps): React.ReactElement {
  const { t } = useTranslation();

  // Replicate guarded margin math for display (INV-1: no NaN/Infinity)
  const profit = roundTo(billedTotal - baseCostTotal, 2);
  const marginPercent =
    billedTotal > 0
      ? roundTo(((billedTotal - baseCostTotal) / billedTotal) * 100, 1)
      : 0;

  const marginColor =
    marginPercent >= 0
      ? 'text-green-600 dark:text-green-400'
      : 'text-amber-600 dark:text-amber-400';

  // -------------------------------------------------------------------
  // State A: Not entitled — locked upgrade card (MARGIN-01, D-05)
  // -------------------------------------------------------------------
  if (!entitled) {
    return (
      <Card>
        <CardContent className="p-6">
          <Lock
            size={20}
            className="text-slate-400 mb-2"
            aria-hidden="true"
          />
          <p className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-1">
            {t('billing.margin_locked_title', 'Analytics — Pro only')}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            {t(
              'billing.margin_locked_body',
              'Margin analytics is available in the Pro plan. Track profit on every project.',
            )}
          </p>
          <Button variant="default" size="sm" onClick={onUpgradeClick}>
            {t('billing.margin_locked_cta', 'Unlock analytics')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // -------------------------------------------------------------------
  // State C: Pro user, no estimate data
  // -------------------------------------------------------------------
  if (billedTotal === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-4">
            {t('billing.margin_section_title', 'Margin analytics')}
          </p>
          <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-6">
            {t(
              'billing.margin_empty',
              'Add items to the estimate to see analytics.',
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  // -------------------------------------------------------------------
  // State B: Pro user with estimate data — metric grid
  // -------------------------------------------------------------------
  return (
    <Card>
      <CardContent className="p-6">
        <p className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-4">
          {t('billing.margin_section_title', 'Margin analytics')}
        </p>

        <dl className="grid grid-cols-2 gap-4">
          {/* Billed amount */}
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {t('billing.margin_billed', 'Billed amount')}
            </dt>
            <dd className="text-lg font-bold text-slate-900 dark:text-slate-50">
              {formatPLN(billedTotal)} {currency}
            </dd>
          </div>

          {/* Base cost */}
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {t('billing.margin_cost', 'Base cost')}
            </dt>
            <dd className="text-lg font-bold text-slate-900 dark:text-slate-50">
              {formatPLN(baseCostTotal)} {currency}
            </dd>
          </div>

          {/* Profit */}
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {t('billing.margin_profit_label', 'Profit')}
            </dt>
            <dd className="text-lg font-bold text-slate-900 dark:text-slate-50">
              {formatPLN(profit)} {currency}
            </dd>
          </div>

          {/* Margin % — focal point with semantic color */}
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {t('billing.margin_percent_label', 'Margin')}
            </dt>
            <dd className={`text-lg font-bold ${marginColor}`}>
              {formatMargin(marginPercent)}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}
